import React, { useState, useEffect } from 'react';
import { getTaxonomy } from '../lib/productTaxonomy';
import { smartQuoteService } from '../services/smartQuoteService';
import { getDimensionConfig, validateDimensions } from '../lib/dimensionConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage, db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import FlamingoLoader from './FlamingoLoader';

const SmartQuoteWizard = ({ onComplete, onCancel, currentComponent = null, editingComponent = null, componentName = null, projectId = null, componentId = null }) => {
  // Always start at step 0 now (which is Select Product)
  const [currentStep, setCurrentStep] = useState(0);
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quoteData, setQuoteData] = useState({
    componentName: componentName || '',
    product: null,
    category: null,
    subcategory: null,
    features: [],
    additionals: [],
    dimensions: {},
    fabric: {
      description: '',
      images: []
    },
    componentPhotos: [],
    materials: [],
    description: ''
  });
  const [enhancedDescription, setEnhancedDescription] = useState('');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const [showTypewriter, setShowTypewriter] = useState(false);
  const [typewriterTimer, setTypewriterTimer] = useState(null);
  const [dimensionConfig, setDimensionConfig] = useState(null);
  const [dimensionValues, setDimensionValues] = useState({});
  const [dimensionErrors, setDimensionErrors] = useState({});
  const [isNavigating, setIsNavigating] = useState(false);
  const [userInteracting, setUserInteracting] = useState(false);
  const [uploadingFabric, setUploadingFabric] = useState(false);
  const [uploadingComponent, setUploadingComponent] = useState(false);
  const [uploadedFabricUrls, setUploadedFabricUrls] = useState([]);
  const [uploadedComponentUrls, setUploadedComponentUrls] = useState([]);
  const [fabricFlamingoKey, setFabricFlamingoKey] = useState(0);
  const [componentFlamingoKey, setComponentFlamingoKey] = useState(0);

  useEffect(() => {
    loadTaxonomy();
  }, []);

  // Debug logging for props
  useEffect(() => {
    console.log('=== SmartQuoteWizard Props ===');
    console.log('projectId:', projectId);
    console.log('componentId:', componentId);
    console.log('projectId type:', typeof projectId);
    console.log('componentId type:', typeof componentId);
    console.log('projectId truthy?', !!projectId);
    console.log('componentId truthy?', !!componentId);
    
    if (!projectId) {
      console.error('❌ NO PROJECT ID - this will cause uploads to fail');
    }
    if (!componentId) {
      console.error('❌ NO COMPONENT ID - this will cause uploads to fail');
    }
    if (projectId && componentId) {
      console.log('✅ Both IDs available - uploads should work');
    }
  }, [projectId, componentId]);

  // Pre-populate data when editing
  useEffect(() => {
    if (editingComponent && editingComponent.taxonomyData && !quoteData.product) {
      const taxonomyData = editingComponent.taxonomyData;
      setQuoteData({
        componentName: editingComponent.name || '',
        product: taxonomyData.product,
        category: taxonomyData.category,
        subcategory: taxonomyData.subcategory,
        features: taxonomyData.features || [],
        additionals: taxonomyData.additionals || [],
        dimensions: taxonomyData.dimensions || {},
        fabric: {
          description: taxonomyData.fabric?.description || '',
          images: []
        },
        componentPhotos: [],
        materials: taxonomyData.materials || [],
        description: taxonomyData.description || ''
      });
      
      if (taxonomyData.dimensions) {
        setDimensionValues(taxonomyData.dimensions);
      }
    }
  }, [editingComponent, quoteData.product]);

  // Pre-populate category if component is provided
  useEffect(() => {
    if (currentComponent && taxonomy && !quoteData.category) {
      // Try to find matching category in taxonomy
      const matchingCategory = taxonomy.categories?.find(cat => 
        cat.key.toLowerCase() === currentComponent.category.toLowerCase()
      );
      
      if (matchingCategory) {
        setQuoteData(prev => ({
          ...prev,
          category: matchingCategory
        }));
      }
    }
  }, [currentComponent, taxonomy]);

  // Auto-load dimension config when reaching dimensions step
  useEffect(() => {
    if (currentStep === 6 && !dimensionConfig && quoteData.subcategory) {
      const loadDimensionConfig = async () => {
        const path = [
          'products',
          quoteData.product?.key,
          quoteData.category?.key,
          quoteData.subcategory?.key,
          ...(quoteData.features.length > 0 ? [quoteData.features[0].key] : [])
        ].filter(Boolean).join('.');
        
        console.log('Auto-loading dimension config for path:', path);
        const config = await getDimensionConfig(path);
        if (config) {
          setDimensionConfig(config);
          // Initialize dimension values
          const initialValues = {};
          Object.keys(config.dimensions || {}).forEach(key => {
            initialValues[key] = '';
          });
          setDimensionValues(initialValues);
        } else {
          // No config found, set empty config to proceed
          setDimensionConfig({ dimensions: {} });
        }
      };
      
      loadDimensionConfig();
    }
  }, [currentStep, dimensionConfig, quoteData.subcategory, quoteData.product, quoteData.category, quoteData.features]);

  const loadTaxonomy = async () => {
    try {
      const data = await getTaxonomy();
      setTaxonomy(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading taxonomy:', error);
      setLoading(false);
    }
  };

  // Upload fabric images immediately
  const uploadFabricImages = async (files) => {
    console.log('=== uploadFabricImages called ===');
    console.log('files:', files);
    console.log('projectId:', projectId, 'type:', typeof projectId);
    console.log('componentId:', componentId, 'type:', typeof componentId);
    console.log('projectId check:', !projectId);
    console.log('componentId check:', !componentId);
    
    if (!projectId || !componentId) {
      console.warn('No projectId or componentId provided, storing files locally');
      console.warn('projectId value:', projectId);
      console.warn('componentId value:', componentId);
      setQuoteData(prev => ({
        ...prev,
        fabric: {
          ...prev.fabric,
          images: [...(prev.fabric?.images || []), ...files]
        }
      }));
      return;
    }

    setUploadingFabric(true);
    setFabricFlamingoKey(Date.now()); // Unique key for variety
    console.log('Uploading fabric images immediately...');

    try {
      const uploadPromises = files.map(async (file, index) => {
        const fileName = `fabric_${Date.now()}_${index}_${file.name}`;
        const path = `projects/${projectId}/components/${componentId}/fabric/${fileName}`;
        
        const storageRef = ref(storage, path);
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        return new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            null,
            (error) => reject(error),
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                
                // Save to Firestore
                await addDoc(collection(db, 'fabricPhotos'), {
                  projectId,
                  componentId,
                  componentName: quoteData.componentName,
                  url: downloadURL,
                  fileName: file.name,
                  path,
                  uploadedAt: serverTimestamp()
                });
                
                resolve(downloadURL);
              } catch (error) {
                reject(error);
              }
            }
          );
        });
      });
      
      const urls = await Promise.all(uploadPromises);
      setUploadedFabricUrls(prev => [...prev, ...urls]);
      console.log('Fabric images uploaded successfully:', urls);
      
    } catch (error) {
      console.error('Error uploading fabric images:', error);
    } finally {
      setUploadingFabric(false);
    }
  };

  // Upload component photos immediately  
  const uploadComponentPhotos = async (files) => {
    console.log('=== uploadComponentPhotos called ===');
    console.log('files:', files);
    console.log('projectId:', projectId, 'type:', typeof projectId);
    console.log('componentId:', componentId, 'type:', typeof componentId);
    
    if (!projectId || !componentId) {
      console.warn('No projectId or componentId provided, storing files locally');
      console.warn('projectId value:', projectId);
      console.warn('componentId value:', componentId);
      setQuoteData(prev => ({
        ...prev,
        componentPhotos: [...(prev.componentPhotos || []), ...files]
      }));
      return;
    }

    setUploadingComponent(true);
    setComponentFlamingoKey(Date.now()); // Unique key for variety
    console.log('Uploading component photos immediately...');

    try {
      const uploadPromises = files.map(async (file, index) => {
        const fileName = `component_${Date.now()}_${index}_${file.name}`;
        const path = `projects/${projectId}/components/${componentId}/${fileName}`;
        
        const storageRef = ref(storage, path);
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        return new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            null,
            (error) => reject(error),
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                
                // Save to Firestore
                console.log(`💾 Saving component photo to database:`, {
                  projectId,
                  componentId, 
                  componentName: quoteData.componentName,
                  url: downloadURL,
                  fileName: file.name
                });
                
                const docRef = await addDoc(collection(db, 'componentPhotos'), {
                  projectId,
                  componentId,
                  componentName: quoteData.componentName,
                  url: downloadURL,
                  fileName: file.name,
                  path,
                  uploadedAt: serverTimestamp()
                });
                
                console.log(`✅ Component photo saved to database with ID:`, docRef.id);
                
                resolve(downloadURL);
              } catch (error) {
                reject(error);
              }
            }
          );
        });
      });
      
      const urls = await Promise.all(uploadPromises);
      setUploadedComponentUrls(prev => [...prev, ...urls]);
      console.log('Component photos uploaded successfully:', urls);
      
    } catch (error) {
      console.error('Error uploading component photos:', error);
    } finally {
      setUploadingComponent(false);
    }
  };

  // Typewriter effect
  const typewriterQuestions = [
    "Hi! Let's create something fabulous. What shall we call this component?",
    "Perfect! Now, what piece are we designing today?",
    "Excellent choice! What style of {product} are we talking about?",
    "Great! What specific type did you have in mind?",
    "Wonderful! Any special features you'd like to include?",
    "Perfect! Let's add some finishing touches.",
    "Now let's get the perfect measurements for your piece.",
    "Time to choose the perfect fabric! What material speaks to you?",
    "Great! Now let's capture some photos of the component or inspiration images.",
    "Almost there! Tell me more about your project.",
    "Fantastic! Let's review what we've created together."
  ];

  const typeWriter = (text, callback) => {
    console.log('typeWriter called with text:', text, 'callback:', !!callback);
    
    // Clear any existing timer first
    if (typewriterTimer) {
      clearInterval(typewriterTimer);
      setTypewriterTimer(null);
    }
    
    setShowTypewriter(true);
    setTypewriterText('');
    
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setTypewriterText(text.substring(0, i + 1)); // Use substring instead of building character by character
        i++;
      } else {
        console.log('typeWriter animation complete, calling callback');
        clearInterval(timer);
        setTypewriterTimer(null);
        if (callback) {
          console.log('Executing callback with 500ms delay');
          setTimeout(callback, 500);
        }
      }
    }, 50);
    
    setTypewriterTimer(timer);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (typewriterTimer) {
        clearInterval(typewriterTimer);
      }
    };
  }, [typewriterTimer]);

  useEffect(() => {
    // Only show initial question on the first step to avoid conflicts with user actions
    if (currentStep === 0) {
      const question = typewriterQuestions[currentStep]?.replace('{product}', quoteData.product?.label || 'item') || '';
      if (question) {
        setTimeout(() => {
          if (!isNavigating && !userInteracting) {
            console.log('Starting initial question typewriter for step:', currentStep);
            typeWriter(question);
          } else {
            console.log('Blocked initial question - userInteracting:', userInteracting, 'isNavigating:', isNavigating);
          }
        }, 500);
      }
    }
  }, [currentStep, quoteData.product]);

  const steps = [
    'Component Name',
    'Select Product',
    'Select Category', 
    'Select Subcategory',
    'Select Features',
    'Select Additionals',
    'Add Dimensions',
    'Add Fabric',
    'Component Photos',
    'Project Additional',
    'Review & Generate'
  ];


  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleProductSelect = (productKey, product) => {
    // Set user interaction flag to prevent initial question from overriding
    setUserInteracting(true);
    
    // Clear any existing typewriter
    if (typewriterTimer) {
      clearInterval(typewriterTimer);
      setTypewriterTimer(null);
    }
    
    setQuoteData({
      ...quoteData,
      product: { key: productKey, ...product },
      category: null,
      subcategory: null,
      features: [],
      additionals: []
    });
    
    console.log('Product selected:', productKey, product.label);
    
    // Immediately navigate to next step
    console.log('About to call handleNext for product');
    handleNext();
    
    // Show confirmation message without waiting for it to complete
    const confirmations = [
      `Wonderful choice! ${product.label} it is. Let's make it fabulous!`,
      `Excellent! I love working with ${product.label} projects.`,
      `Perfect! ${product.label} always adds such style to a space.`
    ];
    const randomConfirmation = confirmations[Math.floor(Math.random() * confirmations.length)];
    
    // Start typewriter but don't wait for it
    typeWriter(randomConfirmation);
    
    // Reset userInteracting after navigation
    setTimeout(() => setUserInteracting(false), 1000);
  };

  const handleCategorySelect = (categoryKey, category) => {
    console.log('=== handleCategorySelect called ===');
    console.log('Category Key:', categoryKey);
    console.log('Category:', category);
    console.log('Current Product:', quoteData.product);
    
    // Set user interaction flag to prevent initial question from overriding
    setUserInteracting(true);
    
    // Clear any existing typewriter
    if (typewriterTimer) {
      clearInterval(typewriterTimer);
      setTypewriterTimer(null);
    }
    
    // Check available subcategories
    const subcategories = taxonomy.products[quoteData.product.key]?.[categoryKey] || {};
    console.log('Raw subcategories object:', subcategories);
    
    const validSubcategories = Object.entries(subcategories).filter(([key, value]) => {
      const isValid = key !== 'label' && key !== 'description' && key !== 'image' && 
             key !== 'archived' && key !== 'archivedAt' && 
             value && typeof value === 'object' && !Array.isArray(value);
      if (!isValid && value) {
        console.log(`Filtered out: ${key}, type: ${typeof value}, isArray: ${Array.isArray(value)}`);
      }
      return isValid;
    });
    
    console.log('Valid subcategories found:', validSubcategories.length);
    console.log('Valid subcategories:', validSubcategories.map(([k, v]) => `${k}: ${v.label || k}`));
    
    const updatedQuoteData = {
      ...quoteData,
      category: { key: categoryKey, ...category },
      subcategory: null,
      features: [],
      additionals: []
    };
    
    // If only one subcategory or none, auto-select and potentially skip further
    if (validSubcategories.length === 0) {
      // No subcategories - skip directly to dimensions
      setQuoteData({
        ...updatedQuoteData,
        subcategory: { key: 'default', label: category.label }
      });
      
      setIsNavigating(true);
      setCurrentStep(6); // Skip to dimensions (step 6 after adding Component Name step)
      console.log('No subcategories found, skipping to dimensions');
      
      // Load dimension config - path needs "products" prefix to match taxonomy
      const path = ['products', quoteData.product?.key, categoryKey].filter(Boolean).join('.');
      if (!dimensionConfig) {
        getDimensionConfig(path).then(config => {
          console.log('Loaded dimension config for path:', path, config);
          setDimensionConfig(config);
          if (config) {
            const initialValues = {};
            Object.keys(config.dimensions || {}).forEach(key => {
              initialValues[key] = '';
            });
            setDimensionValues(initialValues);
          }
        }).catch(error => {
          console.error('Error loading dimension config:', error);
        });
      }
      
      setTimeout(() => {
        setIsNavigating(false);
        setUserInteracting(false);
      }, 1000);
      
    } else if (validSubcategories.length === 1) {
      // Only one subcategory - auto-select it and continue with subcategory logic
      const [subcatKey, subcategory] = validSubcategories[0];
      handleSubcategorySelect(subcatKey, subcategory);
      return;
      
    } else {
      // Multiple subcategories - proceed to subcategory selection
      console.log('Multiple subcategories found, updating state and moving to subcategory step');
      console.log('Current step before update:', currentStep);
      setQuoteData(updatedQuoteData);
      
      // Only advance if we're not already on the subcategory step
      if (currentStep === 2) {
        handleNext();
      } else {
        console.warn('Already on subcategory step or beyond, not advancing');
      }
    }
    
    // Show confirmation message without waiting for it to complete
    const confirmations = [
      `Great choice! ${category.label} is always stunning.`,
      `Perfect! I can already envision this ${category.label}.`,
      `Lovely! ${category.label} will be beautiful.`
    ];
    const randomConfirmation = confirmations[Math.floor(Math.random() * confirmations.length)];
    typeWriter(randomConfirmation);
    
    // Reset userInteracting after navigation
    if (validSubcategories.length !== 1) { // Don't reset if we're calling handleSubcategorySelect
      setTimeout(() => setUserInteracting(false), 1000);
    }
  };

  const handleSubcategorySelect = (subcategoryKey, subcategory) => {
    console.log('=== handleSubcategorySelect called ===');
    console.log('Subcategory Key:', subcategoryKey);
    console.log('Subcategory:', subcategory);
    console.log('Current Category:', quoteData.category);
    
    // Set user interaction flag to prevent initial question from overriding
    setUserInteracting(true);
    
    // Clear any existing typewriter
    if (typewriterTimer) {
      clearInterval(typewriterTimer);
      setTypewriterTimer(null);
    }
    
    // Update quote data
    const updatedQuoteData = {
      ...quoteData,
      subcategory: { key: subcategoryKey, ...subcategory },
      features: [],
      additionals: []
    };
    setQuoteData(updatedQuoteData);
    
    // Check if features are available for this subcategory
    const features = taxonomy.products[updatedQuoteData.product.key]?.[updatedQuoteData.category.key]?.[subcategoryKey] || {};
    const hasFeatures = Object.keys(features).filter(key => {
      const value = features[key];
      return key !== 'label' && key !== 'description' && key !== 'image' && 
             key !== 'archived' && key !== 'archivedAt' && 
             value && typeof value === 'object' && !Array.isArray(value);
    }).length > 0;
    
    // Always navigate immediately after subcategory selection
    
    // Navigate based on available features
    setIsNavigating(true);
    const nextStep = hasFeatures ? 4 : 5; // Go to Features (4) or Additionals (5), never skip to Dimensions
    console.log('Subcategory selected, moving to step:', nextStep, 'hasFeatures:', hasFeatures);
    setCurrentStep(nextStep);
    
    
    // Show confirmation message without waiting for it to complete
    const confirmations = [
      `Excellent! ${subcategory.label} is such a classic choice.`,
      `Wonderful! ${subcategory.label} will be perfect.`,
      `Fabulous! I love ${subcategory.label} style.`
    ];
    const randomConfirmation = confirmations[Math.floor(Math.random() * confirmations.length)];
    typeWriter(randomConfirmation);
    
    // Reset navigation flag and user interaction
    setTimeout(() => {
      setIsNavigating(false);
      setUserInteracting(false);
    }, 1000);
  };

  const handleFeatureToggle = (featureKey, feature) => {
    const existing = quoteData.features.find(f => f.key === featureKey);
    if (existing) {
      setQuoteData({
        ...quoteData,
        features: quoteData.features.filter(f => f.key !== featureKey)
      });
    } else {
      setQuoteData({
        ...quoteData,
        features: [...quoteData.features, { key: featureKey, ...feature }]
      });
    }
  };

  const generateProfessionalDescription = () => {
    setIsEnhancing(true);
    
    const parts = [];
    
    // Professional description structure
    if (quoteData.product?.label && quoteData.category?.label && quoteData.subcategory?.label) {
      parts.push(`Professional ${quoteData.subcategory.label.toLowerCase()} service for ${quoteData.product.label.toLowerCase()}.`);
    }
    
    // Add features professionally
    if (quoteData.features.length > 0) {
      const featureText = quoteData.features.map(f => f.label || f.key).join(', ');
      parts.push(`Including ${featureText.toLowerCase()}.`);
    }
    
    // Add materials/fabric professionally
    if (quoteData.fabric.description || quoteData.materials.length > 0) {
      const materialText = quoteData.materials.length > 0 ? 
        `Using ${quoteData.materials.join(' and ').toLowerCase()}.` : 
        quoteData.fabric.description ? `With custom fabric selection.` : '';
      if (materialText) parts.push(materialText);
    }
    
    // Add component photos if provided
    if (quoteData.componentPhotos?.length > 0) {
      parts.push(`Reference images provided for accurate specification matching.`);
    }
    
    // Add dimensions if available
    const dimensionEntries = Object.entries(quoteData.dimensions || {}).filter(([_, value]) => value);
    if (dimensionEntries.length > 0) {
      const dimText = dimensionEntries.map(([key, value]) => `${key.toLowerCase()}: ${value}`).join(', ');
      parts.push(`Dimensions: ${dimText}.`);
    }
    
    // Add additionals
    if (quoteData.additionals.length > 0) {
      const additionalText = quoteData.additionals.map(a => a.label || a.key).join(', ');
      parts.push(`Additional services: ${additionalText.toLowerCase()}.`);
    }
    
    // Professional closing
    parts.push('All work completed to Pink Flamingo Interiors professional standards with quality materials and expert craftsmanship.');
    
    const professionalDescription = parts.join(' ');
    setEnhancedDescription(professionalDescription);
    
    setTimeout(() => {
      setIsEnhancing(false);
    }, 800); // Brief delay to show generation
  };

  const generateProjectDescription = () => {
    const parts = [];
    
    // Add main taxonomy path
    if (quoteData.product?.label) parts.push(`Product: ${quoteData.product.label}`);
    if (quoteData.category?.label) parts.push(`Category: ${quoteData.category.label}`);
    if (quoteData.subcategory?.label) parts.push(`Type: ${quoteData.subcategory.label}`);
    
    // Add selected features
    if (quoteData.features.length > 0) {
      const featureNames = quoteData.features.map(f => f.label || f.key).join(', ');
      parts.push(`Features: ${featureNames}`);
    }
    
    // Add selected additionals
    if (quoteData.additionals.length > 0) {
      const additionalNames = quoteData.additionals.map(a => a.label || a.key).join(', ');
      parts.push(`Specifications: ${additionalNames}`);
    }
    
    // Add dimensions
    if (quoteData.dimensions && Object.keys(quoteData.dimensions).length > 0) {
      const dimensionsList = Object.entries(quoteData.dimensions).map(([key, value]) => {
        const dimConfig = dimensionConfig?.dimensions?.[key];
        return dimConfig ? `${dimConfig.label}: ${value}${dimConfig.unit}` : `${key}: ${value}`;
      }).join(', ');
      parts.push(`Dimensions: ${dimensionsList}`);
    }
    
    // Add fabric
    if (quoteData.fabric?.description?.trim()) {
      parts.push(`Fabric: ${quoteData.fabric.description}`);
    }
    
    // Add component photos info
    if (quoteData.componentPhotos?.length > 0) {
      parts.push(`Component Photos: ${quoteData.componentPhotos.length} image(s) provided`);
    }
    
    return parts.join('\n');
  };

  // Check if features are available for current selection
  const hasAvailableFeatures = () => {
    if (!quoteData.subcategory || !taxonomy?.products) return false;
    
    const features = taxonomy.products[quoteData.product.key]?.[quoteData.category.key]?.[quoteData.subcategory.key] || {};
    return Object.keys(features).filter(key => {
      const value = features[key];
      return key !== 'label' && key !== 'description' && key !== 'image' && 
             key !== 'archived' && key !== 'archivedAt' && 
             value && typeof value === 'object' && !Array.isArray(value);
    }).length > 0;
  };

  // Check if additional specs are available
  const hasAvailableAdditionals = () => {
    if (quoteData.features.length === 0) return false;
    
    const additionals = quoteData.features.reduce((acc, feature) => {
      const featureData = taxonomy.products[quoteData.product.key]?.[quoteData.category.key]?.[quoteData.subcategory.key]?.[feature.key];
      if (featureData) {
        Object.keys(featureData).forEach(key => {
          const value = featureData[key];
          if (key !== 'label' && key !== 'description' && key !== 'image' && 
              key !== 'archived' && key !== 'archivedAt' && 
              value && typeof value === 'object' && !Array.isArray(value)) {
            acc[key] = value;
          }
        });
      }
      return acc;
    }, {});
    
    return Object.keys(additionals).length > 0;
  };

  // Auto-generate description when reaching final step
  const handleNext = async () => {
    console.log('handleNext called, currentStep:', currentStep, 'isNavigating:', isNavigating);
    
    if (isNavigating || currentStep >= steps.length - 1) {
      console.log('handleNext blocked - isNavigating:', isNavigating, 'currentStep:', currentStep);
      return;
    }
    
    // Clear any existing typewriter immediately
    if (typewriterTimer) {
      clearInterval(typewriterTimer);
      setTypewriterTimer(null);
    }
    
    setIsNavigating(true);
    let nextStep = currentStep + 1;
    
    // Skip features step if no features available - go to additionals instead
    if (currentStep === 3 && !hasAvailableFeatures()) {
      nextStep = 5; // Go to additionals step (5) instead of skipping to dimensions
      console.log('Skipping features step - no features available, going to additionals');
    }
    // From features (4), always go to additionals (5)
    else if (currentStep === 4) {
      nextStep = 5; // Always go to additionals after features
      console.log('Moving from features to additionals');
    }
    // Skip additionals step if no additionals available - go to dimensions 
    else if (currentStep === 5 && !hasAvailableAdditionals()) {
      nextStep = 6; // Skip to dimensions step
      console.log('Skipping additionals step - no additional specs available');
    }
    
    console.log('Moving to step:', nextStep);
    setCurrentStep(nextStep);
    
    try {
      // Auto-load dimension config when reaching Dimensions step
      if (nextStep === 4 && !dimensionConfig) {
        const path = [
          'products',
          quoteData.product?.key,
          quoteData.category?.key,
          quoteData.subcategory?.key,
          ...(quoteData.features.length > 0 ? [quoteData.features[0].key] : [])
        ].filter(Boolean).join('.');
        
        try {
          const config = await getDimensionConfig(path);
          setDimensionConfig(config || { dimensions: {} });
        } catch (error) {
          console.error('Error loading dimension config:', error);
          setDimensionConfig({ dimensions: {} });
        }
      }
      
      // Don't auto-populate description - let user write their own
      // The structured data will go into projectDescription field later
    } finally {
      setIsNavigating(false);
    }
  };

  const handleComplete = () => {
    const finalQuoteData = {
      ...quoteData,
      description: enhancedDescription || quoteData.description,
      generatedAt: new Date().toISOString(),
      uploadedFabricUrls,
      uploadedComponentUrls
    };
    onComplete(finalQuoteData);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
        <span className="ml-2 text-gray-600">Loading product options...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white p-6 rounded-t-lg">
        <h2 className="text-2xl font-bold mb-2">
          🦩 Smart Wizard
          {currentComponent && (
            <span className="ml-3 text-lg font-normal bg-white bg-opacity-20 px-3 py-1 rounded-full text-gray-800">
              {currentComponent.name}
            </span>
          )}
        </h2>
        <p className="text-pink-100">
          {currentComponent 
            ? `Configure specifications for: ${currentComponent.name} (${currentComponent.category})`
            : "Let's create something fabulous together!"
          }
        </p>
      </div>

      {/* Progress Bar */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${index <= currentStep 
                  ? 'bg-pink-600 text-white' 
                  : 'bg-gray-200 text-gray-600'}
              `}>
                {index + 1}
              </div>
              <span className={`ml-2 text-sm ${index <= currentStep ? 'text-pink-600 font-medium' : 'text-gray-500'}`}>
                {step}
              </span>
              {index < steps.length - 1 && (
                <div className={`w-12 h-0.5 ml-2 ${index < currentStep ? 'bg-pink-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Flamingo Assistant */}
      {showTypewriter && (
        <div className="mx-6 mt-6 mb-4 bg-gradient-to-r from-pink-50 to-pink-100 border-l-4 border-pink-500 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center">
                <span className="text-white text-lg">🦩</span>
              </div>
            </div>
            <div className="flex-1">
              <div className="text-pink-800 font-medium text-sm mb-1">Pink Flamingo Assistant</div>
              <div className="text-pink-700 text-sm">
                {typewriterText}
                <span className="animate-pulse">|</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-6 min-h-[400px]">
        {/* Step 0: Component Name */}
        {currentStep === 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What would you like to call this component?</h3>
            <input
              type="text"
              value={quoteData.componentName}
              onChange={(e) => setQuoteData(prev => ({ ...prev, componentName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              placeholder="e.g., Living Room Sofa, Master Bedroom Curtains"
              autoFocus
            />
            <p className="text-sm text-gray-600 mt-2">
              Give this component a descriptive name to help identify it in your project.
            </p>
          </div>
        )}

        {/* Step 1: Select Product */}
        {currentStep === 1 && taxonomy?.products && (
          <div>
            <h3 className="text-lg font-semibold mb-4">What are we creating today?</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(taxonomy.products).map(([key, product]) => (
                <button
                  key={key}
                  onClick={() => {
                    console.log('Button clicked for product:', key, product.label);
                    handleProductSelect(key, product);
                  }}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-pink-500 hover:bg-pink-50 transition-colors text-left"
                >
                  <h4 className="font-medium text-gray-800">{product.label}</h4>
                  {product.description && (
                    <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Category */}
        {currentStep === 2 && quoteData.product && taxonomy?.products && (
          <div>
            <h3 className="text-lg font-semibold mb-4">
              What type of {quoteData.product.label}?
            </h3>
            {/* Debug: Show what's available at this level */}
            {console.log('=== Step 2: Category Selection ===')}
            {console.log('Product key:', quoteData.product.key)}
            {console.log('Looking at:', taxonomy.products[quoteData.product.key])}
            {console.log('All keys at this level:', Object.keys(taxonomy.products[quoteData.product.key] || {}))}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(taxonomy.products[quoteData.product.key] || {})
                .filter(([key, value]) => {
                  return key !== 'label' && key !== 'description' && key !== 'image' && 
                         key !== 'archived' && key !== 'archivedAt' && 
                         value && typeof value === 'object' && !Array.isArray(value);
                })
                .map(([key, category]) => (
                  <button
                    key={key}
                    onClick={() => handleCategorySelect(key, category)}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-pink-500 hover:bg-pink-50 transition-colors text-left"
                  >
                    <h4 className="font-medium text-gray-800">{category.label || key}</h4>
                    {category.description && (
                      <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                    )}
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Step 3: Select Subcategory */}
        {currentStep === 3 && quoteData.category && taxonomy?.products && (() => {
          console.log('=== Step 3: Subcategory Selection Rendering ===');
          console.log('Current step:', currentStep);
          console.log('Product:', quoteData.product);
          console.log('Category:', quoteData.category);
          
          // Get subcategories and add debugging
          const subcategoriesPath = taxonomy.products[quoteData.product.key]?.[quoteData.category.key] || {};
          console.log('Looking for subcategories at:', `products.${quoteData.product.key}.${quoteData.category.key}`);
          console.log('Raw subcategories object:', subcategoriesPath);
          
          const validSubcategories = Object.entries(subcategoriesPath)
            .filter(([key, value]) => {
              return key !== 'label' && key !== 'description' && key !== 'image' && 
                     key !== 'archived' && key !== 'archivedAt' && 
                     value && typeof value === 'object' && !Array.isArray(value);
            });
          
          // If no valid subcategories found, show a message and auto-skip
          if (validSubcategories.length === 0) {
            console.warn('No subcategories found for:', quoteData.category.label);
            // Auto-navigate to next step
            setTimeout(() => {
              setQuoteData(prev => ({
                ...prev,
                subcategory: { key: 'default', label: quoteData.category.label }
              }));
              setCurrentStep(6); // Skip to dimensions
            }, 100);
            
            return (
              <div className="text-center py-8">
                <p className="text-gray-600">No style options available for {quoteData.category.label}.</p>
                <p className="text-sm text-gray-500 mt-2">Moving to next step...</p>
              </div>
            );
          }
          
          return (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Choose a specific style
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {validSubcategories.map(([key, subcategory]) => (
                  <button
                    key={key}
                    onClick={() => handleSubcategorySelect(key, subcategory)}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-pink-500 hover:bg-pink-50 transition-colors text-left"
                  >
                    <h4 className="font-medium text-gray-800">{subcategory.label || key}</h4>
                    {subcategory.description && (
                      <p className="text-sm text-gray-600 mt-1">{subcategory.description}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Step 4: Select Features */}
        {currentStep === 4 && quoteData.subcategory && taxonomy?.products && (
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Select any special features
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(
                taxonomy.products[quoteData.product.key]?.[quoteData.category.key]?.[quoteData.subcategory.key] || {}
              )
                .filter(([key, value]) => {
                  return key !== 'label' && key !== 'description' && key !== 'image' && 
                         key !== 'archived' && key !== 'archivedAt' && 
                         value && typeof value === 'object' && !Array.isArray(value);
                })
                .map(([key, feature]) => (
                  <button
                    key={key}
                    onClick={() => {
                      // Set user interaction flag to prevent initial question from overriding
                      setUserInteracting(true);
                      
                      // Clear any existing typewriter
                      if (typewriterTimer) {
                        clearInterval(typewriterTimer);
                        setTypewriterTimer(null);
                      }
                      
                      // Reset navigation state to ensure we can progress
                      setIsNavigating(false);
                      
                      setQuoteData({
                        ...quoteData,
                        features: [{ key, ...feature }], // Single selection like other levels
                        additionals: [] // Reset additionals when feature changes
                      });
                      
                      // Immediately navigate to next step
                      setTimeout(() => handleNext(), 100); // Small delay to ensure state updates
                      
                      // Show confirmation message without waiting for it to complete
                      const confirmations = [
                        `Perfect! ${feature.label} is an excellent feature.`,
                        `Great choice! ${feature.label} will add the perfect touch.`,
                        `Wonderful! I love the ${feature.label} detail.`
                      ];
                      const randomConfirmation = confirmations[Math.floor(Math.random() * confirmations.length)];
                      typeWriter(randomConfirmation);
                      
                      // Reset userInteracting after navigation
                      setTimeout(() => setUserInteracting(false), 1000);
                    }}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-pink-500 hover:bg-pink-50 transition-colors text-left"
                  >
                    <h4 className="font-medium text-gray-800">{feature.label || key}</h4>
                    {feature.description && (
                      <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                    )}
                  </button>
                ))}
              {Object.entries(
                taxonomy.products[quoteData.product.key]?.[quoteData.category.key]?.[quoteData.subcategory.key] || {}
              )
                .filter(([key, value]) => {
                  return key !== 'label' && key !== 'description' && key !== 'image' && 
                         key !== 'archived' && key !== 'archivedAt' && 
                         value && typeof value === 'object' && !Array.isArray(value);
                }).length === 0 && (
                <div className="text-center p-8 text-gray-500">
                  <p>No special features available for this selection.</p>
                  <button
                    onClick={() => {
                      // Set user interaction flag to prevent initial question from overriding
                      setUserInteracting(true);
                      
                      // Clear any existing typewriter
                      if (typewriterTimer) {
                        clearInterval(typewriterTimer);
                        setTypewriterTimer(null);
                      }
                      
                      // Immediately navigate to next step
                      handleNext();
                      
                      // Show confirmation message without waiting for it to complete
                      const confirmations = [
                        "No problem! Let's keep the design clean and classic.",
                        "Perfect! Sometimes simplicity is the most elegant choice.",
                        "Excellent! A minimalist approach can be stunning."
                      ];
                      const randomConfirmation = confirmations[Math.floor(Math.random() * confirmations.length)];
                      typeWriter(randomConfirmation);
                      
                      // Reset userInteracting after navigation
                      setTimeout(() => setUserInteracting(false), 1000);
                    }}
                    className="mt-4 px-6 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700"
                  >
                    Continue with No Features
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Select Additionals */}
        {currentStep === 5 && quoteData.subcategory && taxonomy?.products && (
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Select any additional specifications
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(
                // Get additionals from selected features
                quoteData.features.length > 0 
                  ? quoteData.features.reduce((acc, feature) => {
                      const featureData = taxonomy.products[quoteData.product.key]?.[quoteData.category.key]?.[quoteData.subcategory.key]?.[feature.key];
                      if (featureData) {
                        Object.entries(featureData)
                          .filter(([key, value]) => {
                            return key !== 'label' && key !== 'description' && key !== 'image' && 
                                   key !== 'archived' && key !== 'archivedAt' && 
                                   value && typeof value === 'object' && !Array.isArray(value);
                          })
                          .forEach(([key, additional]) => {
                            acc[`${feature.key}_${key}`] = { ...additional, parentFeature: feature.key, originalKey: key };
                          });
                      }
                      return acc;
                    }, {})
                  : {}
              ).map(([key, additional]) => (
                <button
                  key={key}
                  onClick={() => {
                    // Set user interaction flag to prevent initial question from overriding
                    setUserInteracting(true);
                    
                    // Clear any existing typewriter
                    if (typewriterTimer) {
                      clearInterval(typewriterTimer);
                      setTypewriterTimer(null);
                    }
                    
                    // Reset navigation state to ensure we can progress
                    setIsNavigating(false);
                    
                    setQuoteData({
                      ...quoteData,
                      additionals: [{ key: additional.originalKey, ...additional }] // Single selection like other levels
                    });
                    
                    // Immediately navigate to next step
                    setTimeout(() => handleNext(), 100); // Small delay to ensure state updates
                    
                    // Show confirmation message without waiting for it to complete
                    const confirmations = [
                      `Perfect! ${additional.label} is a great addition.`,
                      `Excellent choice! ${additional.label} will complete the look beautifully.`,
                      `Wonderful! ${additional.label} adds that finishing touch.`
                    ];
                    const randomConfirmation = confirmations[Math.floor(Math.random() * confirmations.length)];
                    typeWriter(randomConfirmation);
                    
                    // Reset userInteracting after navigation
                    setTimeout(() => setUserInteracting(false), 1000);
                  }}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-pink-500 hover:bg-pink-50 transition-colors text-left"
                >
                  <h4 className="font-medium text-gray-800">{additional.label || key}</h4>
                  {additional.description && (
                    <p className="text-sm text-gray-600 mt-1">{additional.description}</p>
                  )}
                </button>
              ))}
              {Object.keys(
                quoteData.features.length > 0 
                  ? quoteData.features.reduce((acc, feature) => {
                      const featureData = taxonomy.products[quoteData.product.key]?.[quoteData.category.key]?.[quoteData.subcategory.key]?.[feature.key];
                      if (featureData) {
                        Object.entries(featureData)
                          .filter(([key, value]) => {
                            return key !== 'label' && key !== 'description' && key !== 'image' && 
                                   key !== 'archived' && key !== 'archivedAt' && 
                                   value && typeof value === 'object' && !Array.isArray(value);
                          })
                          .forEach(([key, additional]) => {
                            acc[`${feature.key}_${key}`] = additional;
                          });
                      }
                      return acc;
                    }, {})
                  : {}
              ).length === 0 && (
                <div className="text-center p-8 text-gray-500">
                  <p>No additional specifications available.</p>
                  <button
                    onClick={() => {
                      // Set user interaction flag to prevent initial question from overriding
                      setUserInteracting(true);
                      
                      // Clear any existing typewriter
                      if (typewriterTimer) {
                        clearInterval(typewriterTimer);
                        setTypewriterTimer(null);
                      }
                      
                      // Immediately navigate to next step
                      handleNext();
                      
                      // Show confirmation message without waiting for it to complete
                      const confirmations = [
                        "No problem! Let's continue with the design.",
                        "Perfect! Moving on to the next step.",
                        "Excellent! Let's keep going."
                      ];
                      const randomConfirmation = confirmations[Math.floor(Math.random() * confirmations.length)];
                      typeWriter(randomConfirmation);
                      
                      // Reset userInteracting after navigation
                      setTimeout(() => setUserInteracting(false), 1000);
                    }}
                    className="mt-4 px-6 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700"
                  >
                    Continue
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 6: Add Dimensions */}
        {currentStep === 6 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Specify dimensions</h3>
            
            {/* Auto-load dimension config */}
            {!dimensionConfig && (
              <div className="text-center p-4">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-600"></div>
                  <span className="ml-2 text-gray-600">Loading dimension requirements...</span>
                </div>
              </div>
            )}
            
            {/* Show dimension inputs if config exists */}
            {dimensionConfig && Object.keys(dimensionConfig.dimensions || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(dimensionConfig.dimensions).map(([key, dimConfig]) => (
                  <div key={key} className="border border-gray-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {dimConfig.label}
                      {dimConfig.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    
                    {dimConfig.description && (
                      <p className="text-xs text-gray-600 mb-2">{dimConfig.description}</p>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={dimensionValues[key] || ''}
                        onChange={(e) => {
                          const newValues = { ...dimensionValues, [key]: e.target.value };
                          setDimensionValues(newValues);
                          
                          // Update quote data
                          setQuoteData(prev => ({
                            ...prev,
                            dimensions: newValues
                          }));
                          
                          // Validate
                          const validation = validateDimensions(newValues, dimensionConfig);
                          setDimensionErrors(validation.errors);
                        }}
                        placeholder={dimConfig.placeholder || `Enter ${dimConfig.label.toLowerCase()}`}
                        min={dimConfig.min}
                        max={dimConfig.max}
                        className={`flex-1 px-3 py-2 border ${
                          dimensionErrors[key] ? 'border-red-500' : 'border-gray-300'
                        } rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500`}
                      />
                      <span className="text-sm text-gray-600 min-w-[60px]">
                        {dimConfig.unit}
                      </span>
                    </div>
                    
                    {dimConfig.min !== undefined && dimConfig.max !== undefined && (
                      <p className="text-xs text-gray-500 mt-1">
                        Range: {dimConfig.min} - {dimConfig.max} {dimConfig.unit}
                      </p>
                    )}
                    
                    {dimensionErrors[key] && (
                      <p className="text-xs text-red-600 mt-1">{dimensionErrors[key]}</p>
                    )}
                  </div>
                ))}
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 Tip: Accurate dimensions ensure a perfect fit for your custom piece.
                  </p>
                </div>
              </div>
            ) : dimensionConfig ? (
              <div className="text-center p-8 text-gray-500">
                <p>No dimensions required for this product configuration.</p>
                <p className="text-sm mt-2">Click Next to continue.</p>
              </div>
            ) : null}
          </div>
        )}

        {/* Step 7: Add Fabric */}
        {currentStep === 7 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Choose your fabric</h3>
            <p className="text-sm text-gray-600 mb-4">
              Describe the fabric you'd like to use and upload any reference images.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fabric Description
              </label>
              <textarea
                value={quoteData.fabric?.description || ''}
                onChange={(e) => setQuoteData(prev => ({
                  ...prev,
                  fabric: {
                    ...prev.fabric,
                    description: e.target.value
                  }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                rows={3}
                placeholder="Describe the fabric type, color, pattern, texture, or any specific requirements..."
              />
              <p className="text-xs text-gray-500 mt-1">
                e.g., "Navy blue velvet with gold threading" or "Natural linen in cream color"
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fabric Images (Optional) {uploadingFabric && <span className="text-pink-600">(Uploading...)</span>}
              </label>
              
              {uploadingFabric ? (
                <FlamingoLoader 
                  message="Uploading beautiful fabric images..."
                  key={fabricFlamingoKey}
                />
              ) : (
                <div>
                  <div 
                    className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${uploadingFabric ? 'border-pink-500 bg-pink-50' : 'border-gray-300 hover:border-pink-400'}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-pink-500', 'bg-pink-50');
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-pink-500', 'bg-pink-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-pink-500', 'bg-pink-50');
                      
                      const files = Array.from(e.dataTransfer.files || []).filter(file => 
                        file.type.startsWith('image/')
                      );
                      
                      if (files.length > 0) {
                        uploadFabricImages(files);
                      }
                    }}
                  >
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="fabric-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-pink-600 hover:text-pink-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-pink-500"
                        >
                          <span>Upload fabric images</span>
                          <input
                            id="fabric-upload"
                            name="fabric-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            accept="image/*"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              uploadFabricImages(files);
                            }}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                    </div>
                  </div>
                  
                  {/* Display uploaded fabric images */}
                  {uploadedFabricUrls.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Uploaded Fabric Images ({uploadedFabricUrls.length}):</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {uploadedFabricUrls.map((url, index) => (
                          <div key={index} className="relative">
                            <img
                              src={url}
                              alt={`Fabric ${index + 1}`}
                              className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                            />
                            <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                              ✓
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-green-600 mt-2">✓ Images successfully uploaded to cloud storage</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 Tip: Upload swatches or inspiration photos to help suppliers understand your vision.
              </p>
            </div>
          </div>
        )}

        {/* Step 8: Component Photos */}
        {currentStep === 8 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Component Photos</h3>
            <p className="text-sm text-gray-600 mb-4">
              Upload photos of the component or inspiration images to help suppliers understand your vision.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Component Images {uploadingComponent && <span className="text-pink-600">(Uploading...)</span>}
              </label>
              
              {uploadingComponent ? (
                <FlamingoLoader 
                  message="Uploading stunning component photos..."
                  key={componentFlamingoKey}
                />
              ) : (
                <div>
                  <div 
                    className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${uploadingComponent ? 'border-pink-500 bg-pink-50' : 'border-gray-300 hover:border-pink-400'}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-pink-500', 'bg-pink-50');
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-pink-500', 'bg-pink-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-pink-500', 'bg-pink-50');
                      
                      const files = Array.from(e.dataTransfer.files || []).filter(file => 
                        file.type.startsWith('image/')
                      );
                      
                      if (files.length > 0) {
                        uploadComponentPhotos(files);
                      }
                    }}
                  >
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="component-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-pink-600 hover:text-pink-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-pink-500"
                        >
                          <span>Upload component photos</span>
                          <input
                            id="component-upload"
                            name="component-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            accept="image/*"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              uploadComponentPhotos(files);
                            }}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                    </div>
                  </div>
                  
                  {/* Display uploaded component images */}
                  {uploadedComponentUrls.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Uploaded Component Images ({uploadedComponentUrls.length}):</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {uploadedComponentUrls.map((url, index) => (
                          <div key={index} className="relative">
                            <img
                              src={url}
                              alt={`Component ${index + 1}`}
                              className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                            />
                            <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                              ✓
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-green-600 mt-2">✓ Images successfully uploaded to cloud storage</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 Tip: Include photos from different angles, close-ups of details, and inspiration images to help suppliers create the perfect piece.
              </p>
            </div>
          </div>
        )}

        {/* Step 9: Project Additional */}
        {currentStep === 9 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Project Additional Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Information
                <span className="text-xs text-gray-500 font-normal ml-2">
                  (Optional - any extra details, special requirements, or notes)
                </span>
              </label>
              <textarea
                value={quoteData.description}
                onChange={(e) => setQuoteData({ ...quoteData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                rows={4}
                placeholder="Describe your vision, style preferences, or any special requirements..."
              />
            </div>

          </div>
        )}

        {/* Step 10: Review & Generate */}
        {currentStep === 10 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Review your description</h3>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2">Selected Options:</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li><strong>Product:</strong> {quoteData.product?.label}</li>
                <li><strong>Category:</strong> {quoteData.category?.label}</li>
                <li><strong>Subcategory:</strong> {quoteData.subcategory?.label}</li>
                {quoteData.features.length > 0 && (
                  <li><strong>Features:</strong> {quoteData.features.map(f => f.label).join(', ')}</li>
                )}
                {quoteData.additionals.length > 0 && (
                  <li><strong>Specifications:</strong> {quoteData.additionals.map(a => a.label).join(', ')}</li>
                )}
                {Object.keys(quoteData.dimensions || {}).length > 0 && (
                  <li><strong>Dimensions:</strong> {Object.entries(quoteData.dimensions).map(([key, value]) => {
                    const dimConfig = dimensionConfig?.dimensions?.[key];
                    return dimConfig ? `${dimConfig.label}: ${value}${dimConfig.unit}` : `${key}: ${value}`;
                  }).join(', ')}</li>
                )}
                {quoteData.fabric?.description && (
                  <li><strong>Fabric:</strong> {quoteData.fabric.description}</li>
                )}
                {uploadedFabricUrls.length > 0 && (
                  <li><strong>Fabric Images:</strong> {uploadedFabricUrls.length} image(s) uploaded to cloud storage</li>
                )}
                {uploadedComponentUrls.length > 0 && (
                  <li><strong>Component Photos:</strong> {uploadedComponentUrls.length} image(s) uploaded to cloud storage</li>
                )}
              </ul>
            </div>

            {quoteData.description && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Description:</h4>
                <p className="text-sm text-gray-600">{quoteData.description}</p>
              </div>
            )}

          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
        <button
          onClick={currentStep === 0 ? onCancel : handleBack}
          disabled={uploadingFabric || uploadingComponent}
          className={`px-4 py-2 focus:outline-none ${
            uploadingFabric || uploadingComponent 
              ? 'text-gray-400 cursor-not-allowed' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </button>
        
        {currentStep < steps.length - 1 ? (
          <button
            onClick={handleNext}
            disabled={
              isNavigating ||
              uploadingFabric ||
              uploadingComponent ||
              (currentStep === 0 && !quoteData.componentName) ||
              (currentStep === 1 && !quoteData.product) ||
              (currentStep === 2 && !quoteData.category) ||
              (currentStep === 3 && !quoteData.subcategory) ||
              (currentStep === 5 && dimensionConfig && Object.keys(dimensionConfig.dimensions || {}).length > 0 && !validateDimensions(dimensionValues, dimensionConfig).valid)
            }
            className="px-6 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:opacity-50"
          >
            {uploadingFabric || uploadingComponent ? 'Uploading Images...' : isNavigating ? 'Loading...' : 'Next'}
          </button>
        ) : (
          <button
            onClick={handleComplete}
            disabled={uploadingFabric || uploadingComponent}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
          >
            {uploadingFabric || uploadingComponent ? 'Uploading Images...' : 'Create Description'}
          </button>
        )}
      </div>
    </div>
  );
};

export default SmartQuoteWizard;